import PinSuccessMessage from "@/components/PinSuccessMessage";
import useAppStore from "@/context/store";
import { flashPin } from "@/services";
import { useMemoizedFn } from "ahooks";
import { uniq } from "lodash";
import { useRef } from "react";
import { checkLogin, openBindTelModal } from "../open";

export function usePinCreate({
  pin,
  onPinSuccess,
  onPinWarn,
}: {
  pin: IPin;
  onPinSuccess?: (pin: IPin) => void;
  onPinWarn?: (err: any) => void;
}) {
  const pinitModalRef = useRef(null);

  const { lastBoards, pinIds } = useAppStore(({ lastBoards, pinIds }) => ({
    lastBoards,
    pinIds,
  }));

  /**
   * 快采
   */
  const onFlash = useMemoizedFn(async (check) => {
    const user = await checkLogin();

    // 处理关闭登录弹窗场景
    if (!user) {
      return;
    }

    if (!user?.tel) {
      await openBindTelModal();
    }

    if (!pin) {
      return;
    }

    const boardId = lastBoards?.length > 0 ? lastBoards[0]?.board_id : null;

    const result = await flashPin({
      via: pin.pin_id,
      text: pin.raw_text,
      board_id: boardId,
      ...(check ? { check } : null),
    });

    if (!result.err) {
      if (result.warning) {
        onPinWarn?.(result);
      } else {
        const _pin = result.pin;
        PinSuccessMessage.open({
          pin: _pin,
          onUndo: (pin) => {
            const pinId = pin.pin_id;
            useAppStore.setState(({ pinIds }) => ({
              pinIds: pinIds.filter((pin) => pin.pin_id === pinId),
            }));
          },
        });

        useAppStore.setState(({ pinIds }) => {
          return {
            pinIds: uniq(pinIds.concat([pin.pin_id])),
          };
        });

        onPinSuccess?.(result.pin);
      }
    }
  });

  /**采集 */
  const onCreate = useMemoizedFn(async () => {
    const user = await checkLogin();

    // 处理关闭登录弹窗场景
    if (!user) {
      return;
    }

    if (!user?.tel) {
      // 实时获取用户信息，避免未更新信息而导致弹窗
      const user = await useAppStore.getState().refreshUser();
      if (user?.tel) return user;

      await openBindTelModal();
    }

    const { default: PinCreatorModal } = await import(
      "@/components/PinCreatorModal"
    );

    pinitModalRef.current = PinCreatorModal.open({
      pin,
      onOk: (_pin) => {
        PinSuccessMessage.open({
          pin: _pin,
          onUndo: (pin) => {
            const pinId = pin.pin_id;
            useAppStore.setState({
              pinIds: pinIds.filter((pin) => pin.pin_id === pinId),
            });
          },
        });

        useAppStore.setState(({ pinIds }) => ({
          pinIds: uniq(pinIds.concat([pin.pin_id])),
        }));
        onPinSuccess?.(_pin);

        useAppStore.getState().refreshLastBoards();
      },
      afterClose: () => {
        pinitModalRef.current = null;
      },
    });
  });

  return { onFlash, onCreate, pinitModalRef };
}
